import { COUPON_APPLIES_TO } from '@repo/lib';
import { z } from '../zod.js';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId.js';

export const couponRulesDescription =
  'Where the coupon works and what it discounts, one rule per page. A coupon works on a page when any of its rules names that page, and on each page the rules are combined with OR (an event cannot mix `tickets` and `ticketsAndAddOns` rules). Omit `rules` (and `pageIds`), or send an empty list, for a coupon that discounts the whole order on every checkout page and event.';

export const couponRulePageIdDescription = 'The checkout page or event this rule applies to.';

export const couponRuleAppliesToDescription =
  'What the coupon discounts on the page. Checkout pages: `wholeOrder` (the main product at any price and every order bump), `prices` (applies when the customer buys one of `priceIds`, discounting that price only), `pricesAndBumps` (applies only when the customer buys one of `priceIds` and one of `bumpProductIds`, discounting both). Events: `wholeOrder` (every ticket and add-on), `tickets` (applies when the customer buys one of `ticketTypeIds`, discounting those tickets only), `ticketsAndAddOns` (applies only when the customer buys one of `ticketTypeIds` and one of `bumpProductIds`, discounting both). Setup fees and ticket fees follow `appliesToSetupFee` and `appliesToTicketFees`.';

export const couponRulePriceIdsDescription =
  "Prices of the page's main product, only for `prices` and `pricesAndBumps`. An empty list or no list means every price of the main product, including prices added later. These are the `id`s of a product's `prices`, not Stripe price ids.";

export const couponRuleTicketTypeIdsDescription =
  'Ticket types of the event, only for `tickets` and `ticketsAndAddOns`. An empty list or no list means every ticket type of the event, including ticket types added later.';

export const couponRuleBumpProductIdsDescription =
  'Products of order bumps (checkout pages) or add-ons (events) on the page, only for `pricesAndBumps` and `ticketsAndAddOns`. An empty list or no list means every bump or add-on on the page, including ones added later; the coupon still needs one of them bought.';

export const couponPageIdsDescription =
  'Deprecated: use `rules`. The checkout pages and events this coupon can be used on, each discounting the whole order. Accepted only without `rules`. In responses, the pages named by `rules`.';

export const couponTicketTypeIdsDescription =
  'Deprecated: use `rules`. Ticket types of events in `pageIds`: on such an event the coupon discounts only those tickets. Accepted only without `rules`. In responses, every ticket type named by `rules`.';

export const CouponAppliesToEnum = z.enum(COUPON_APPLIES_TO).meta({
  description: couponRuleAppliesToDescription,
  example: 'prices',
});

export const CouponRuleInputValidationSchema = z.object({
  pageId: ObjectIdStringValidationSchema.meta({ description: couponRulePageIdDescription }),
  appliesTo: CouponAppliesToEnum,
  priceIds: z
    .array(ObjectIdStringValidationSchema)
    .optional()
    .meta({ description: couponRulePriceIdsDescription, example: ['67fcbdac6a91c25ef2d3534a'] }),
  ticketTypeIds: z
    .array(ObjectIdStringValidationSchema)
    .optional()
    .meta({ description: couponRuleTicketTypeIdsDescription }),
  bumpProductIds: z
    .array(ObjectIdStringValidationSchema)
    .optional()
    .meta({ description: couponRuleBumpProductIdsDescription }),
});

export const CouponRuleResponseValidationSchema = z.object({
  pageId: z.string().meta({ description: couponRulePageIdDescription }),
  appliesTo: CouponAppliesToEnum,
  priceIds: z.array(z.string()).meta({ description: couponRulePriceIdsDescription }),
  ticketTypeIds: z.array(z.string()).meta({ description: couponRuleTicketTypeIdsDescription }),
  bumpProductIds: z.array(z.string()).meta({ description: couponRuleBumpProductIdsDescription }),
});

export const rulesWithoutLegacyScope = {
  check: (data: { rules?: unknown; pageIds?: unknown; ticketTypeIds?: unknown }) =>
    data.rules === undefined || (data.pageIds === undefined && data.ticketTypeIds === undefined),
  message: 'Send either rules or pageIds and ticketTypeIds, not both',
};
